import { useEffect, useRef, useState } from "react"
import styles from "./dropdown.module.scss"
import SText from "../Text/SText"
import { useTranslations } from "next-intl"

// List of German health insurances

export default function SkinuvitaDropdown({
  onSelect,
  healthInsurances,
  currentlySelected,
}: {
  onSelect: (value: string) => void
  healthInsurances: string[]
  currentlySelected: string
}) {
  const t = useTranslations("index.finder")
  const [inputValue, setInputValue] = useState(currentlySelected ?? "")
  const [filteredList, setFilteredList] = useState(healthInsurances)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [showDropdown, setShowDropdown] = useState(false)

  // Fuzzy search filter
  const filterResults = (input) => {
    const query = input.toLowerCase()

    // Matches that start with the query first
    const startsWithMatches = healthInsurances.filter((insurance) => insurance.toLowerCase().startsWith(query))

    // Matches that contain the query anywhere (but don't start with it)
    const containsMatches = healthInsurances.filter(
      (insurance) => !insurance.toLowerCase().startsWith(query) && insurance.toLowerCase().includes(query)
    )

    // Prioritize matches that start with input, then append fuzzy matches
    setFilteredList([...startsWithMatches, ...containsMatches])
    setSelectedIndex(-1) // Reset selection when typing
  }

  const dropdownRef = useRef(null)

  useEffect(() => {
    if (dropdownRef.current && selectedIndex !== -1) {
      const listItems = dropdownRef.current.children
      if (listItems[selectedIndex]) {
        const item = listItems[selectedIndex]
        const dropdown = dropdownRef.current

        // Ensure selected item is in view
        const itemOffsetTop = item.offsetTop
        const itemHeight = item.offsetHeight
        const dropdownScrollTop = dropdown.scrollTop
        const dropdownHeight = dropdown.clientHeight

        if (itemOffsetTop < dropdownScrollTop) {
          // Scroll up if item is above view
          dropdown.scrollTop = itemOffsetTop
        } else if (itemOffsetTop + itemHeight > dropdownScrollTop + dropdownHeight) {
          // Scroll down if item is below view
          dropdown.scrollTop = itemOffsetTop + itemHeight - dropdownHeight
        }
      }
    }
  }, [selectedIndex]) // Re-run this effect when selectedIndex changes

  // Handle text input change
  const handleChange = (e) => {
    const value = e.target.value
    setInputValue(value)
    setShowDropdown(value.length > 0)
    filterResults(value)
  }

  // Handle key events (⬆️⬇️ for navigation, ⏎ for selection)
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      setSelectedIndex((prev) => {
        const nextIndex = prev < filteredList.length - 1 ? prev + 1 : prev
        return nextIndex
      })
    } else if (e.key === "ArrowUp") {
      setSelectedIndex((prev) => {
        const nextIndex = prev > 0 ? prev - 1 : prev
        return nextIndex
      })
    } else if (e.key === "Enter" && filteredList.length === 1) {
      setInputValue(filteredList[0])
      onSelect(filteredList[0]) // 🔹 Send value to parent
      setShowDropdown(false)
    } else if (e.key === "Enter" && selectedIndex !== -1) {
      const selectedValue = filteredList[selectedIndex]
      setInputValue(selectedValue)
      onSelect(selectedValue) // 🔹 Send value to parent
      setShowDropdown(false)
    } else if (e.key === "Escape") {
      setShowDropdown(false)
    } else if (e.key === "Tab" && showDropdown) {
      e.preventDefault() // Prevent default tabbing behavior
      setSelectedIndex(0) // Focus first item
    }
  }

  // Handle selection via click
  const handleSelect = (value) => {
    setInputValue(value)
    onSelect(value) // 🔹 Send value to parent
    setTimeout(() => setShowDropdown(false), 100) // ✅ Fix disappearing issue
  }

  const placeholder = t("step1.options.placeholder")

  return (
    <div className={styles.dropdownContainer}>
      <input
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={(e) =>
          setTimeout(() => {
            // dropdownRef.current?.contains(e.relatedTarget) is used, so that the onBlur effect only happens, when the relatedTarget is not the dropdown
            // itself. This is necessary, because the dropdown is not a child of the input field, so the onBlur
            // event would be triggered, when clicking on the dropdown and prevents the onMouseDown event on the
            // dropdown item
            if (!dropdownRef.current?.contains(e.relatedTarget)) {
              setShowDropdown(false)
            }
          }, 100)
        }
        onFocus={() => setShowDropdown(inputValue.length > 0)}
        placeholder={placeholder}
        className={`${styles.inputField} ${showDropdown ? styles.inputOpen : ""}`}
      />
      {showDropdown && (
        <ul className={styles.dropdownList} ref={dropdownRef}>
          {filteredList.map((insurance, index) => (
            <li
              tabIndex={index === selectedIndex ? 0 : -1} // Only selected item is tabbable
              key={insurance}
              className={`${styles.dropdownItem} ${index === selectedIndex ? styles.activeItem : ""}`}
              onMouseDown={() => handleSelect(insurance)} // ✅ Fix for disappearing dropdown
            >
              <SText color={"green400"} variant={"body"}>
                {insurance}
              </SText>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
